import type { NextRequest, NextResponse } from 'next/server'

import { configPublic } from '@src/config.public'
import { AuthProviders, COOKIES, QUERY_PARAMS } from '@src/utils/constants.util'
import { isomorphicCookies } from '@src/utils/cookies.util'

export const AuthUtils = {
  getAuthProviders,
  getCookieNames,
  getQueryParamNames,
  getIsCheckingSSO,
  clearCheckingSSO,
  getIsComingFromCallback,
  clearComingFromCallback,
  getIsSSOCheckedRecently,
}

function getAuthProviders(): AuthProviders[] {
  return Object.values(AuthProviders)
}

function getCookieNames(authProvider?: AuthProviders) {
  switch (authProvider ?? configPublic.general.authProvider) {
    case AuthProviders.AKAMAI:
      return {
        authSession: COOKIES.akamaiSession,
        authCodeVerifier: COOKIES.akamaiCodeVerifier,
        authCallbackUrl: COOKIES.akamaiCallbackUrl,
        authRedirectUrl: COOKIES.akamaiRedirectUrl,
        authComingFromCallback: COOKIES.akamaiComingFromCallback,
        authCheckingSSO: COOKIES.akamaiCheckingSSO,
        authSSOCheckedRecently: COOKIES.akamaiSSOCheckedRecently,
      }
    case AuthProviders.AUTH0:
      return {
        authSession: COOKIES.auth0Session,
        authCallbackUrl: COOKIES.auth0CallbackUrl,
        authRedirectUrl: COOKIES.auth0RedirectUrl,
        authComingFromCallback: COOKIES.auth0ComingFromCallback,
        authCheckingSSO: COOKIES.auth0CheckingSSO,
        authSSOCheckedRecently: COOKIES.auth0SSOCheckedRecently,
      }
    default:
      throw new Error(`Unsupported auth provider: ${configPublic.general.authProvider}`)
  }
}

function getQueryParamNames() {
  return {
    redirectUrl: QUERY_PARAMS.redirectUrl,
    locale: QUERY_PARAMS.authLocale,
  }
}

async function getIsCheckingSSO(request?: NextRequest): Promise<boolean> {
  const authCookieNames = getCookieNames()
  const cookies = isomorphicCookies({ request })
  return await cookies.has(authCookieNames.authCheckingSSO)
}

async function clearCheckingSSO(request?: NextRequest, response?: NextResponse) {
  const authCookieNames = getCookieNames()
  const cookies = isomorphicCookies({ request, response })
  await cookies.delete(authCookieNames.authCheckingSSO)
}

async function getIsComingFromCallback(request?: NextRequest): Promise<boolean> {
  const authCookieNames = getCookieNames()
  const cookies = isomorphicCookies({ request })
  return await cookies.has(authCookieNames.authComingFromCallback)
}

async function clearComingFromCallback(request?: NextRequest, response?: NextResponse) {
  const authCookieNames = getCookieNames()
  const cookies = isomorphicCookies({ request, response })
  await cookies.delete(authCookieNames.authComingFromCallback)
}

async function getIsSSOCheckedRecently(request?: NextRequest): Promise<boolean> {
  const authCookieNames = getCookieNames()
  const cookies = isomorphicCookies({ request })
  return await cookies.has(authCookieNames.authSSOCheckedRecently)
}
